import { styles } from "@repo/helpers";
import { BasicDialog, Button, ButtonTypes, TextBox } from "@repo/ui";
import { useQuery } from "@tanstack/react-query";
import { Command, Search } from "lucide-react";
import React, { FC, useState } from "react";

import * as Queries from "../../api/queries";
import { useRepSearchApi } from "../../api/repSearchApi";
import { useUpdateRep } from "../../helpers/urlHooks";
import RepPortalSearchItem from "../../models/repPortalSearchItem";
import { SearchResults } from "./searchResults";
import style from "./style.module.scss";

interface SearchDialogProps {
	searchOpen: boolean;
	setSearchOpen: (open: boolean) => void;
}

export const SearchDialog: FC<SearchDialogProps> = ({ searchOpen, setSearchOpen }) => {
	const [search, setSearch] = useState<string>("");
	const [currentItems, setCurrentItems] = useState<RepPortalSearchItem[]>([]);
	const updateRep = useUpdateRep();
	const { loadSearches } = useRepSearchApi();
	const repSearchQuery = useQuery({
		queryKey: Queries.REP_PORTAL_META_SEARCH,
		queryFn: async () => {
			const search = await loadSearches();

			return new SearchResults(search);
		},
	});

	function searchUpdated(search: string) {
		if (repSearchQuery.isLoading) return;

		setSearch(search);
		setCurrentItems(repSearchQuery.data.search(search));
	}

	const onClickItem = async (item: RepPortalSearchItem) => {
		if (item.entityType === "Agents") {
			await updateRep(item.repId);
			return;
		}

		await updateRep(item.repId, item.entityType, item.entityId);
	};

	function renderSearchItems(items: RepPortalSearchItem[]) {
		if (!items) return null;
		const map = items.groupBy((i) => i.entityType);
		const keys = Array.from(map.keys());
		return keys.map((o) => renderSearchItemGroup(o, map.get(o)));
	}

	function renderSearchItemGroup(group: string, items: RepPortalSearchItem[]) {
		return (
			<section id={group} key={group}>
				<h6>{group}</h6>
				<div>{items.map(renderSearchItem)}</div>
			</section>
		);
	}

	function renderSearchItem(item: RepPortalSearchItem) {
		return (
			<Button key={item.entityId} types={[ButtonTypes.Text]} onClick={() => onClickItem(item)}>
				{item.displayText}
			</Button>
		);
	}

	return (
		<BasicDialog title="Search" open={searchOpen} onOpenChange={setSearchOpen} hideTrigger>
			<div className={style.searchDialog}>
				<div className={style.searchDialogHeader}>
					<TextBox
						label=""
						placeholder="Search"
						className={style.searchDialogInput}
						value={search}
						changeHandler={searchUpdated}
					/>
				</div>
				<div className={style.searchDialogContent}>
					{currentItems.length === 0 ? (
						<div className={style.searchDialogEmpty}>No results found</div>
					) : (
						renderSearchItems(currentItems)
					)}
				</div>
			</div>
		</BasicDialog>
	);
};

export const SearchButton = () => {
	const [searchOpen, setSearchOpen] = useState(false);

	return (
		<>
			<Button className={styles(style.searchButton, style.searchButtonMd)} onClick={() => setSearchOpen(true)}>
				<div className={style.searchButtonContent}>
					<Search className={style.searchButtonIcon} />
					<span className={styles(style.searchButtonText, style.searchButtonTextMd)}>Search...</span>
				</div>
				<div className={styles(style.searchButtonShortcut, style.searchButtonShortcutMd)}>
					<Command className={style.searchButtonShortcutIcon} />
					<span>K</span>
				</div>
			</Button>
			<SearchDialog searchOpen={searchOpen} setSearchOpen={setSearchOpen} />
		</>
	);
};
